import React, { useEffect, useState } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { DashboardCardCP } from 'app/screens/screen-home/components/DashboardCardCP'
import { DashboardFilterTP } from 'app/screens/screen-home/inner/DashboardFilterTP'
import { DashboardLinksUtils } from 'app/screens/screen-home/inner/DashboardLinksUtils'
import { ThemeProject } from 'config/theme/project/ThemeProject'
import { ChartsRequests } from 'submodules/nerit-aquatec-sdk/services/charts/ChartsRequests'
import { NonConformityChartResponseDTO } from 'submodules/nerit-aquatec-sdk/services/charts/dtos/responses/NonConformityChartResponseDTO'
import { EmptyCP } from 'submodules/nerit-framework-ui/common/components/empty/EmptyCP'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { RequestHelper } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestHelper'
import { IApiReturn } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/types/IApiReturn'

/** Quantidade de parâmetros exibidos para caber no card; a lista completa fica na página do gráfico. */
const MAX_FIELDS = 8
const BAR_HEIGHT = 34

interface ICPProps {
    filter: DashboardFilterTP
}

/**
 * Gráfico de Não Conformidade por parâmetro (o mesmo da ferramenta), compacto para o card.
 */
export function DashboardNonConformityCardCP(props: ICPProps): JSX.Element {

    const [data, setData] = useState<NonConformityChartResponseDTO[]>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    useEffect(() => { void load() }, [props.filter])

    /**
     */
    async function load(): Promise<void> {

        setIsLoading(true)
        try {
            const response = await RequestHelper.runRequest<IApiReturn<NonConformityChartResponseDTO[]>>(
                ChartsRequests.getNonConformityChart({
                    locationCodes: props.filter.locationCodes,
                    regulationCode: props.filter.regulationCode,
                    dateRange: props.filter.dateRange,
                }),
            )
            setData(response.data.data ?? [])
        } catch {
            setData(undefined)
            NotificationHelper.error(NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
        } finally {
            setIsLoading(false)
        }
    }

    const visibleData = (data ?? []).slice(0, MAX_FIELDS)

    return (
        <DashboardCardCP
            title={'Não conformidades por parâmetro'}
            subtitle={!!props.filter.regulationName ? `Norma · ${props.filter.regulationName}` : undefined}
            isLoading={isLoading}
            minHeight={320}
            link={{
                label: 'Abrir gráfico',
                onClick: () => DashboardLinksUtils.goToNonConformity(props.filter),
            }}
        >
            {
                !!data && visibleData.length === 0 &&
                <EmptyCP description={'Nenhuma não conformidade no período'}/>
            }
            {
                visibleData.length > 0 &&
                <HighchartsReact
                    highcharts={Highcharts}
                    options={{
                        chart: { type: 'bar', height: Math.max((visibleData.length * BAR_HEIGHT) + 60, 160) },
                        credits: { enabled: false },
                        title: { text: '' },
                        legend: { enabled: false },
                        exporting: { enabled: false },
                        xAxis: { categories: visibleData.map((item) => item.fieldName) },
                        yAxis: { title: { text: '%' }, max: 100 },
                        tooltip: { valueSuffix: '%' },
                        plotOptions: {
                            bar: {
                                color: ThemeProject.error,
                                dataLabels: { enabled: true, format: '{y}%' },
                            },
                        },
                        series: [{
                            type: 'bar',
                            name: 'Percentual de Não Conformidades',
                            data: visibleData.map((item) => item.nonConformityPercent),
                        }],
                    }}
                />
            }
        </DashboardCardCP>
    )
}
